import { useState, useRef, useCallback } from 'react'
import { useHaptics } from '../utils/haptics'
import { useNotifications } from '../contexts/NotificationContext'
import axios from 'axios'
import { API_BASE_URL } from '../config/api'

export function useVideoMode() {
  const { showError, showSuccess } = useNotifications()
  const haptics = useHaptics()
  const [isVideoMode, setIsVideoMode] = useState(false)
  const [isInspectionMode, setIsInspectionMode] = useState(false)
  const [videoStream, setVideoStream] = useState(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const inspectionIntervalRef = useRef(null)
  const realtimeSocketRef = useRef(null)

  // Capture a frame from the video stream
  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null

    const video = videoRef.current
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    // Set canvas size to match video
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Convert to base64 image
    return canvas.toDataURL('image/jpeg', 0.8)
  }, [])

  // Send frame to GPT-4o Vision for analysis
  const analyzeFrame = useCallback(async (frameDataUrl, addMessage, realtimeSocket) => {
    try {
      console.log('📸 Capturing frame for AI analysis...')

      // Convert data URL to blob for multipart upload
      const base64Data = frameDataUrl.split(',')[1]
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'image/jpeg' })

      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('image', blob, 'inspection-frame.jpg')

      // Get auth token
      const token = localStorage.getItem('token')

      // Send to backend for GPT-4o Vision analysis
      const response = await axios.post(`${API_BASE_URL}/api/realtime/analyze-image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      })

      const analysis = response.data.analysis

      console.log('🤖 AI Vision Analysis:', analysis)

      // If in realtime mode, inject the analysis into the conversation
      if (realtimeSocket && realtimeSocket.connected) {
        console.log('📤 Injecting vision analysis into realtime conversation')
        
        realtimeSocket.emit('send_message', {
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              {
                type: 'input_text',
                text: `[AI Vision Analysis of live video feed]: ${analysis}. Based on this visual inspection, please provide your assessment and recommendations.`
              }
            ]
          }
        })

        // Trigger AI response
        realtimeSocket.emit('send_message', {
          type: 'response.create'
        })
      } else {
        // Fallback: add as regular message
        addMessage({
          role: 'assistant',
          content: `📹 **Live Inspection Analysis:**\n\n${analysis}`,
          type: 'text'
        })
      }

    } catch (error) {
      console.error('❌ Frame analysis error:', error)
    }
  }, [])

  // Start Inspection Mode (video + realtime AI analysis)
  const startInspectionMode = async (addMessage, realtimeSocket) => {
    try {
      setIsVideoMode(true)
      setIsInspectionMode(true)
      haptics.light()
      
      // Store realtime socket reference
      realtimeSocketRef.current = realtimeSocket

      // Request camera access (rear camera for inspection)
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',  // Use rear camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }, 
        audio: false // Audio is already handled by realtime mode
      })
      
      setVideoStream(stream)
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }

      // Create hidden canvas for frame capture
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas')
      }

      // Add instruction message
      addMessage({
        role: 'system',
        content: '🎥 **Inspection Mode Activated**\n\nI\'m now analyzing your live video feed. Please:\n\n1. **Point your camera** at the issue you want to inspect\n2. **Move slowly** to give me time to analyze\n3. **Get close-ups** of specific areas\n4. **Speak naturally** to describe what you see\n\nI\'ll provide real-time analysis and recommendations as you inspect.',
        type: 'system'
      })

      // Analyze frames every 5 seconds (to avoid overwhelming the AI)
      inspectionIntervalRef.current = setInterval(() => {
        const frame = captureFrame()
        if (frame) {
          analyzeFrame(frame, addMessage, realtimeSocket)
        }
      }, 5000) // Every 5 seconds

      console.log('✅ Inspection Mode started with real-time AI analysis')
      showSuccess('Inspection Mode activated. AI is analyzing your video feed.')

    } catch (error) {
      console.error('❌ Camera access failed:', error)
      showError('Camera access failed. Please check permissions.')
      setIsVideoMode(false)
      setIsInspectionMode(false)
    }
  }

  // Regular video mode (without AI analysis)
  const startVideoMode = async (addMessage) => {
    try {
      setIsVideoMode(true)
      setIsInspectionMode(false)
      haptics.light()
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }, 
        audio: true 
      })
      
      setVideoStream(stream)
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }

      addMessage({
        role: 'system',
        content: '📹 Live video started. Recording...',
        type: 'system'
      })

    } catch (error) {
      showError('Camera access failed. Please check permissions.')
      setIsVideoMode(false)
    }
  }

  const endVideoMode = (addMessage) => {
    setIsVideoMode(false)
    setIsInspectionMode(false)
    
    // Clear inspection interval
    if (inspectionIntervalRef.current) {
      clearInterval(inspectionIntervalRef.current)
      inspectionIntervalRef.current = null
    }

    // Stop video stream
    if (videoStream) {
      console.log('🎥 STOPPING VIDEO: Releasing all video and audio tracks')
      videoStream.getTracks().forEach(track => {
        console.log(`Stopping ${track.kind} track:`, track.label)
        track.stop()
      })
      setVideoStream(null)
      console.log('✅ VIDEO STOPPED: All media tracks released')
    }

    // Clear realtime socket reference
    realtimeSocketRef.current = null

    addMessage({
      role: 'system',
      content: '🎥 Inspection session ended.',
      type: 'system'
    })
  }

  // Manual frame capture (for "Take Photo" button)
  const captureAndAnalyze = useCallback(async (addMessage, realtimeSocket) => {
    const frame = captureFrame()
    if (frame) {
      haptics.medium()
      showSuccess('Photo captured! Analyzing...')
      await analyzeFrame(frame, addMessage, realtimeSocket)
    }
  }, [captureFrame, analyzeFrame, haptics, showSuccess])

  return {
    isVideoMode,
    isInspectionMode,
    videoStream,
    videoRef,
    startVideoMode,
    startInspectionMode,
    endVideoMode,
    captureAndAnalyze
  }
}
